/**
 * Client wrapper for server-side patient display-name crypto
 * ─────────────────────────────────────────────────────────
 * Encryption, decryption, and search of `display_name` all happen entirely
 * server-side (functions/src/crypto/patientCrypto.ts) - the master key
 * never reaches the browser. These are thin callable wrappers over that
 * function, plus an in-memory plaintext cache keyed by patientId. Each cache
 * entry carries the ciphertext it was derived from, so a caller that later
 * observes a different ciphertext for the same patientId (the record was
 * re-encrypted) transparently re-fetches instead of returning a stale name.
 */
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';

/** @type {Map<string, { ciphertext: string | undefined, displayName: string }>} */
const plaintextCache = new Map();

function getCallable() {
  const app = getApp();
  const region = import.meta.env.VITE_LOCATION || 'europe-west9';
  const functions = getFunctions(app, region);
  return httpsCallable(functions, 'patientCrypto');
}

/**
 * Encrypts a plaintext patient display name server-side. The HKDF salt is
 * the caller's own verified uid, derived server-side from the auth token -
 * never supplied by the client.
 * @param {string} plaintext
 * @returns {Promise<string>} "enc:v1:<base64>" ciphertext
 */
export async function encryptDisplayName(plaintext) {
  if (!plaintext) throw new Error('encryptDisplayName: plaintext must be a non-empty string');

  const fn = getCallable();
  const result = await fn({ action: 'encryptDisplayName', plaintext });
  const ciphertext = /** @type {{ ciphertext?: string }} */ (result.data)?.ciphertext;
  if (!ciphertext) throw new Error('encryptDisplayName: server returned no ciphertext');
  return ciphertext;
}

/**
 * Decrypts a single patient's display name server-side.
 * @param {string} patientId
 * @param {string} [ciphertext] the doc's current display_name value, used only
 *   to detect a stale cache entry; omit if unknown (skips that check).
 * @returns {Promise<string>}
 */
export async function decryptDisplayName(patientId, ciphertext) {
  if (!patientId) throw new Error('decryptDisplayName: patientId is required');

  const cached = plaintextCache.get(patientId);
  if (cached && cached.ciphertext === ciphertext) return cached.displayName;

  const fn = getCallable();
  const result = await fn({ action: 'decryptDisplayName', patientId });
  const displayName = /** @type {{ displayName?: string }} */ (result.data)?.displayName;
  if (displayName === undefined) throw new Error('decryptDisplayName: server returned no displayName');

  plaintextCache.set(patientId, { ciphertext, displayName });
  return displayName;
}

/**
 * Batch-decrypts a page's worth of patient names in one round trip. Only ids
 * missing (or stale) in the cache are actually sent to the server.
 * @param {Array<{ patientId: string, ciphertext?: string }>} patients
 * @returns {Promise<Record<string, string | null>>} patientId -> displayName (null = denied or failed)
 */
export async function decryptDisplayNames(patients) {
  /** @type {Record<string, string | null>} */
  const results = {};
  const toFetch = [];

  for (const { patientId, ciphertext } of patients) {
    const cached = plaintextCache.get(patientId);
    if (cached && cached.ciphertext === ciphertext) {
      results[patientId] = cached.displayName;
    } else {
      toFetch.push({ patientId, ciphertext });
    }
  }

  if (toFetch.length > 0) {
    const fn = getCallable();
    const result = await fn({ action: 'decryptDisplayNames', patientIds: toFetch.map((p) => p.patientId) });
    const fetched = /** @type {{ results?: Record<string, string | null> }} */ (result.data)?.results ?? {};

    for (const { patientId, ciphertext } of toFetch) {
      const displayName = fetched[patientId] ?? null;
      results[patientId] = displayName;
      if (displayName !== null) {
        plaintextCache.set(patientId, { ciphertext, displayName });
      }
    }
  }

  return results;
}

/**
 * Server-side search over the caller's own patients (or the shared demo
 * record for demo callers) - the client never sees ciphertext it can't
 * already read. Warms the plaintext cache with every match.
 * @param {string} term
 * @returns {Promise<{
 *   results: Array<{ patientId: string, displayName: string, ciphertext: string, createdTime: number | null, examDate: string | null }>,
 *   truncated: boolean,
 * }>}
 */
export async function searchDisplayNames(term) {
  if (!term || !term.trim()) throw new Error('searchDisplayNames: term must be a non-empty string');

  const fn = getCallable();
  const result = await fn({ action: 'searchDisplayNames', term });
  const data = /** @type {{ results?: any[], truncated?: boolean }} */ (result.data) ?? {};
  const results = data.results ?? [];

  for (const match of results) {
    plaintextCache.set(match.patientId, { ciphertext: match.ciphertext, displayName: match.displayName });
  }

  return { results, truncated: Boolean(data.truncated) };
}

/** @internal exposed for test isolation only - never import in application code */
export const _plaintextCacheForTesting = plaintextCache;
